import useDraftQuoteResponse from '@shared/components/draft-response/hooks/useDraftQuoteResponse';
import { useToast } from '@shared/components/toast';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { createContext, useCallback } from 'react';
import { useEmailThreadContext } from './pluginContexts/EmailThreadContext';

export type ResponseDraftContextType = {
  draft?: string;
  draftIsLoading?: boolean;

  draftReply?: (fallbackItemId?: string) => void;
  draftReplyInProgress?: boolean;

  composeMessage?: () => void;
  composeMessageInProgress?: boolean;

  trackingQuoteId?: string;
  batchCount?: number;

  refetchDraft?: () => void;

  itemId?: string;
};

export const ResponseDraftContext = createContext<ResponseDraftContextType>({
  draft: undefined,
  draftIsLoading: false,
  draftReply: undefined,
  draftReplyInProgress: false,
  composeMessage: undefined,
  composeMessageInProgress: false,
  trackingQuoteId: undefined,
  batchCount: undefined,
});

export const ResponseDraftContextProvider = ({
  quoteIds,
  itemId,
  respondToEmail,
  children,
  originatorId,
}: {
  quoteIds: string[];
  originatorId?: string;
  itemId?: string;
  respondToEmail?: string;
  children: React.ReactNode;
}) => {
  const { sendToast } = useToast();
  const { reply } = useEmailThreadContext();
  const { draftReplyAll, loadingDraftReplyAll: draftReplyInProgress } = reply;
  const { track } = useAnalytics();
  const {
    message: draft,
    isLoading: draftIsLoading,
    refetch: refetchDraft,
  } = useDraftQuoteResponse({
    quoteIds,
    originatorId,
  });

  // NOTE(max): The API only supports associating one quote
  // with the tracking event for now.
  const trackingQuoteId = quoteIds[0];
  const batchCount = quoteIds.length;
  const draftReply = async (fallbackItemId?: string) => {
    const replyToItemId = itemId || fallbackItemId;
    if (!replyToItemId) {
      sendToast('Original message not found', { variant: 'error' });
      return;
    }

    track('Clicked Draft Reply All', {
      quoteId: trackingQuoteId,
      count: batchCount,
      data: {
        itemId,
        fallbackItemId,
      },
    });
    await draftReplyAll(replyToItemId, draft);
  };

  const composeEmail = useCallback(() => {
    if (!draft) {
      sendToast('Draft message not found', { variant: 'error' });
      return;
    }

    if (!respondToEmail) {
      sendToast('Respond to email address not found', { variant: 'error' });
      return;
    }

    track('Clicked Compose Email', {
      quoteId: trackingQuoteId,
      count: batchCount,
    });
    window.open(
      `mailto:${respondToEmail}?subject=${encodeURI(
        'Re: Upcoming Shipment'
      )}&body=${encodeURI(draft)}`
    );
  }, [respondToEmail, draft, quoteIds, trackingQuoteId, batchCount]);

  return (
    <ResponseDraftContext.Provider
      value={{
        draft,
        draftIsLoading,
        draftReply: draft ? draftReply : undefined,
        draftReplyInProgress,
        refetchDraft,
        // NOTE(max): In the future we may want to support other message types.
        composeMessage: composeEmail,
        composeMessageInProgress: false,
        trackingQuoteId,
        batchCount,
        itemId,
      }}
    >
      {children}
    </ResponseDraftContext.Provider>
  );
};
